/**
 * localStorage-backed score queue for offline-first persistence.
 * Scores are written here immediately on every mutation, then drained
 * to Supabase by the sync engine. Queue items survive app kills,
 * WebView recycling, and network outages.
 *
 * An in-memory cache (`memQueue`) avoids JSON.parse on every render.
 * Writes go to both memory and localStorage; reads come from memory.
 */

import logger from './logger'

const QUEUE_KEY = 'press_score_queue'
const GAME_CACHE_PREFIX = 'press_game_cache_'

// In-memory mirror of the localStorage queue — avoids JSON.parse per render
let memQueue = null

function readQueue() {
  if (memQueue !== null) return memQueue
  try {
    memQueue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]')
  } catch {
    memQueue = []
  }
  return memQueue
}

function writeQueue(queue) {
  memQueue = queue
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
  window.dispatchEvent(new CustomEvent('score-queue-changed'))
}

let nextId = Date.now()

/**
 * Add a score mutation to the queue.
 * Deduplicates on (game_id, player_id, hole_number, type) — newer replaces older.
 */
export function enqueueScore({ type, game_id, player_id, hole_number, strokes, score_taken }) {
  const queue = [...readQueue()]
  const idx = queue.findIndex(
    (item) =>
      item.game_id === game_id &&
      item.player_id === player_id &&
      item.hole_number === hole_number &&
      item.type === type
  )

  const entry = {
    id: String(++nextId),
    type, // "upsert" | "delete" | "score_taken"
    game_id,
    player_id,
    hole_number,
    ...(strokes !== undefined && { strokes }),
    ...(score_taken !== undefined && { score_taken }),
    status: 'pending',
    retries: 0,
    created_at: Date.now(),
    updated_at: Date.now(),
  }

  if (idx >= 0) {
    entry.id = queue[idx].id // keep original id
    queue[idx] = entry
  } else {
    queue.push(entry)
  }

  writeQueue(queue)
  logger.sync('Score enqueued', { type, gameId: game_id, playerId: player_id, hole: hole_number, queueLength: queue.length })
  return entry
}

export function dequeueScore(id) {
  const queue = readQueue().filter((item) => item.id !== id)
  writeQueue(queue)
}

export function getPendingForGame(gameId) {
  return readQueue().filter((item) => item.game_id === gameId)
}

export function getQueueStats() {
  const queue = readQueue()
  const pending = queue.filter((item) => item.status === 'pending').length
  const syncing = queue.filter((item) => item.status === 'syncing').length
  return { pending, syncing, total: queue.length }
}

export function markSyncing(id) {
  const queue = [...readQueue()]
  const item = queue.find((i) => i.id === id)
  if (item) {
    item.status = 'syncing'
    item.updated_at = Date.now()
    writeQueue(queue)
  }
}

export function markPending(id) {
  const queue = [...readQueue()]
  const item = queue.find((i) => i.id === id)
  if (item) {
    item.status = 'pending'
    item.retries = (item.retries || 0) + 1
    item.updated_at = Date.now()
    writeQueue(queue)
  }
}

/**
 * Reset any items stuck in 'syncing' back to 'pending'.
 * This recovers from app kills / backgrounding that interrupted an in-flight API call.
 * Safe because all queue operations (upsert, delete, score_taken) are idempotent.
 * Returns the number of items recovered.
 */
export function recoverStuckSyncing() {
  const queue = [...readQueue()]
  let recovered = 0
  for (const item of queue) {
    if (item.status === 'syncing') {
      item.status = 'pending'
      item.updated_at = Date.now()
      recovered++
    }
  }
  if (recovered > 0) {
    writeQueue(queue)
  }
  return recovered
}

// ---------------------------------------------------------------------------
// Merge server scores with pending queue
// ---------------------------------------------------------------------------

/**
 * Merge server-side scores array with any pending queue items for this game.
 * Queue items win on (player_id, hole_number) conflicts.
 * Delete items remove from the merged result.
 */
export function mergeScoresWithQueue(serverScores, gameId) {
  const pending = getPendingForGame(gameId)
  if (pending.length === 0) return serverScores

  // Start from server scores, indexed by key
  const merged = new Map()
  for (const s of serverScores) {
    merged.set(`${s.player_id}_${s.hole_number}`, { ...s })
  }

  // Apply queue items in order
  for (const item of pending) {
    const key = `${item.player_id}_${item.hole_number}`
    if (item.type === 'delete') {
      merged.delete(key)
    } else if (item.type === 'upsert') {
      const existing = merged.get(key) || {}
      merged.set(key, {
        ...existing,
        game_id: item.game_id,
        player_id: item.player_id,
        hole_number: item.hole_number,
        strokes: item.strokes,
      })
    } else if (item.type === 'score_taken') {
      const existing = merged.get(key)
      if (existing) {
        merged.set(key, { ...existing, score_taken: item.score_taken })
      }
    }
  }

  return Array.from(merged.values())
}

// ---------------------------------------------------------------------------
// Game data cache (eliminates loading spinners on resume)
// ---------------------------------------------------------------------------

export function cacheGameData(gameId, data) {
  try {
    localStorage.setItem(GAME_CACHE_PREFIX + gameId, JSON.stringify({ _ts: Date.now(), v: data }))
  } catch {
    // localStorage full — silently fail, this is best-effort
  }
}

export function getCachedGameData(gameId) {
  try {
    const raw = localStorage.getItem(GAME_CACHE_PREFIX + gameId)
    if (!raw) return undefined
    const parsed = JSON.parse(raw)
    // Wrapped { _ts, v } format (current) or raw value (legacy)
    return parsed && typeof parsed === 'object' && '_ts' in parsed && 'v' in parsed
      ? parsed.v
      : parsed
  } catch {
    return undefined
  }
}

/**
 * Clean up cache for a specific game (e.g., when game is completed/deleted).
 */
export function clearGameCache(gameId) {
  try {
    localStorage.removeItem(GAME_CACHE_PREFIX + gameId)
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Generic query cache (for critical queries that must survive app kill)
// ---------------------------------------------------------------------------

const QUERY_CACHE_PREFIX = 'press_qcache_'

/**
 * Cache any query result to localStorage under a stable key.
 * Used as placeholderData so the app renders instantly after a force-kill
 * even with no network.
 */
export function cacheQueryData(key, data) {
  try {
    localStorage.setItem(QUERY_CACHE_PREFIX + key, JSON.stringify({ _ts: Date.now(), v: data }))
  } catch {
    // best-effort
  }
}

export function getCachedQueryData(key) {
  try {
    const raw = localStorage.getItem(QUERY_CACHE_PREFIX + key)
    if (!raw) return undefined
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && '_ts' in parsed && 'v' in parsed
      ? parsed.v
      : parsed
  } catch {
    return undefined
  }
}

/**
 * Evict cache entries older than maxAgeMs to keep localStorage from growing
 * unbounded as the user accumulates rounds. Inspects the `_ts` field on
 * wrapped { _ts, v } cache entries; legacy entries (raw value) are left in
 * place — they'll be rewritten with a timestamp on next access.
 *
 * Best-effort — runs once on app start. Failure is silent.
 */
export function pruneOldCaches(maxAgeMs = 30 * 24 * 60 * 60 * 1000) {
  try {
    const now = Date.now()
    const keysToDelete = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k) continue
      if (!k.startsWith(QUERY_CACHE_PREFIX) && !k.startsWith(GAME_CACHE_PREFIX)) continue
      try {
        const raw = localStorage.getItem(k)
        if (!raw) continue
        const parsed = JSON.parse(raw)
        const ts = parsed?._ts
        if (typeof ts === 'number' && now - ts > maxAgeMs) {
          keysToDelete.push(k)
        }
      } catch {
        keysToDelete.push(k)
      }
    }
    for (const k of keysToDelete) {
      try { localStorage.removeItem(k) } catch { /* ignore */ }
    }
    if (keysToDelete.length > 0) {
      logger.info('Pruned old caches', { count: keysToDelete.length })
    }
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Debug helpers (accessible from browser console via window.__scoreQueue)
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  window.__scoreQueue = {
    inspect: () => readQueue(),
    clear: () => writeQueue([]),
    stats: () => getQueueStats(),
  }
}
